import type { Immutable } from "immer";
import React from "react";
import { getGlobalSpacingClasses } from "tsi-common-react/src/components/_spacing/globalSpacing";

import type { ProductSpecificationsBlock as ProductSpecificationsBlockValue } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { PRODUCT_COMPONENTS } from "../../blocks/ProductSpecifications/index";

import styles from "../../styles/composable/product-specifications.module.scss";

type Value = ProductSpecificationsBlockValue;
type Row = NonNullable<Value["rows"]>[number];

/**
 * Faithful port of the legacy server-rendered
 * ``cms/blocks/product-specifications-block.html`` (the tempurpedic-local
 * ProductSpecificationsBlock; the shared #33410 covers the libs variant).
 *
 * Each row owns its open/close state (the AccordionBlock precedent): a
 * ``<section class="product-specifications-block ...">`` wrapper, a
 * ``.l-max-width`` container with a ``<header>`` (the ``header_content`` rich
 * text) and a ``.rows`` list. Each row is a ``.row`` with an
 * ``<h4 class="product-specifications-block__heading">``
 * ``<button class="accordion">`` toggle (``aria-controls`` / ``aria-expanded``)
 * and an ``#product-specifications-{slug}-{n}`` ``.accordion-target`` panel
 * (``aria-hidden``) whose per-product spec table is rendered NATIVELY in React
 * (#33410) -- the legacy server-rendered ``html`` is gone; each row dispatches
 * on its ``template_name`` to a component in the ``ProductSpecifications``
 * ``PRODUCT_COMPONENTS`` registry. ``row.is_expanded`` seeds the initial state.
 *
 * The accordion id slug (``{{ row.title|slugify }}``) and the ``n`` (the
 * 1-based ``forloop.counter``) match the legacy template; the slug is computed
 * server-side with Django ``slugify`` and shipped on each row. The slug is NOT
 * a usable dispatch key (``LuxeBreeze`` and ``LuxeBreezeSplit`` both slugify to
 * ``luxebreeze``), so dispatch uses ``template_name`` (the partial basename).
 */
const SpecRow = (
    props: Immutable<{ row: Row; counter: number }>,
): React.JSX.Element => {
    const { row, counter } = props;
    const targetId = `product-specifications-${row.slug}-${counter}`;
    const Product = PRODUCT_COMPONENTS[row.template_name];
    const [isExpanded, setIsExpanded] = React.useState(!!row.is_expanded);
    return (
        <div className={concatClassNames(["row", styles.row])}>
            <h4
                className={concatClassNames([
                    "product-specifications-block__heading",
                    styles.heading,
                ])}
            >
                <button
                    type="button"
                    className={concatClassNames([
                        "accordion",
                        isExpanded ? "accordion--is-active" : "",
                    ])}
                    aria-controls={targetId}
                    aria-expanded={isExpanded ? "true" : "false"}
                    onClick={(event) => {
                        event.preventDefault();
                        setIsExpanded((value) => !value);
                    }}
                >
                    {row.title}
                </button>
            </h4>
            <div
                id={targetId}
                className={concatClassNames([
                    "accordion-target",
                    isExpanded ? "accordion-target--is-active" : "",
                ])}
                aria-hidden={isExpanded ? "false" : "true"}
            >
                {Product ? <Product /> : null}
            </div>
        </div>
    );
};

export const ProductSpecificationsBlock = (
    props: Immutable<{ value: ProductSpecificationsBlockValue }>,
): React.JSX.Element => {
    const v = props.value;
    const rows = v.rows ?? [];
    return (
        <section
            className={concatClassNames([
                "product-specifications-block",
                styles.productSpecificationsBlock,
                v.background_style?.style ?? "",
                getGlobalSpacingClasses(v.spacing),
            ])}
        >
            <div className="l-max-width">
                <header>
                    <RichTextBlock
                        value={v.header_content ?? ""}
                        className="rich-text"
                    />
                </header>

                <div className={concatClassNames(["rows", styles.rows])}>
                    {rows.map((row, i) => (
                        <SpecRow key={i} row={row} counter={i + 1} />
                    ))}
                </div>
            </div>
        </section>
    );
};
